/**
 * 教师调校 — spec-school-student-admin.md §6.3 (P1)。
 * 唯一教师的班级必须指定接手人(阻断级);协同班级直接解绑。
 * 后端二次校验(TEACHERLESS_CLASSES 422)——这里的下拉只是把
 * 必填做在前面,不是校验的替身。
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useSchoolClasses,
  useSchoolTeachers,
  useSchools,
  useTransferTeachers,
} from "@/api/school";

export function TeacherTransferDialog({
  fromSchoolId,
  teacher,
  onClose,
}: {
  fromSchoolId: string;
  teacher: { id: string; name: string; classes: string[] };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const schools = useSchools();
  const classes = useSchoolClasses(fromSchoolId);
  const teachers = useSchoolTeachers(fromSchoolId);
  const transfer = useTransferTeachers();
  const [toSchool, setToSchool] = useState("");
  const [successors, setSuccessors] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // 该教师是唯一教师的班级:需要接手人
  const soloClasses = useMemo(
    () =>
      (classes.data ?? []).filter(
        (c) => c.teachers.length === 1 && c.teachers[0] === teacher.name,
      ),
    [classes.data, teacher.name],
  );
  const candidates = (teachers.data ?? []).filter((x) => x.id !== teacher.id);
  const targetSchools = (schools.data ?? []).filter((s) => s.id !== fromSchoolId);
  const allAssigned = soloClasses.every((c) => successors[c.id]);

  async function submit() {
    setErr(null);
    try {
      await transfer.mutateAsync({
        teacher_ids: [teacher.id],
        to_school_id: toSchool,
        class_reassignments: successors,
        reason: reason || undefined,
      });
      setDone(true);
    } catch (e) {
      const d = (e as { detail?: { message?: string; error?: string } }).detail;
      setErr(d?.message ?? d?.error ?? (e as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-[28rem] rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold text-slate-800 mb-3">
          {t("teacherTransfer.title", { name: teacher.name })}
        </h2>

        {done ? (
          <>
            <p className="mb-4 text-sm text-emerald-700">{t("teacherTransfer.done")}</p>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm">
                {t("transfer.done")}
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="block text-sm mb-3">
              <span className="text-slate-600">{t("transfer.targetSchool")}</span>
              <select
                value={toSchool}
                onChange={(e) => setToSchool(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              >
                <option value="">—</option>
                {targetSchools.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>

            {soloClasses.length > 0 && (
              <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 p-3">
                <p className="text-xs text-rose-700 mb-2">{t("teacherTransfer.soloWarning", { n: soloClasses.length })}</p>
                {soloClasses.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 mb-1.5 text-xs text-slate-700">
                    <span className="w-28 truncate">{c.name}</span>
                    <select
                      value={successors[c.id] ?? ""}
                      onChange={(e) => setSuccessors((m) => ({ ...m, [c.id]: e.target.value }))}
                      className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                    >
                      <option value="">{t("teacherTransfer.pickSuccessor")}</option>
                      {candidates.map((x) => (
                        <option key={x.id} value={x.id}>{x.name || x.email}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            )}

            <label className="block text-sm mb-4">
              <span className="text-slate-600">{t("transfer.reason")}</span>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              />
            </label>
            {err && <p className="mb-2 text-xs text-rose-600">{err}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">
                {t("common.cancel")}
              </button>
              <button
                disabled={!toSchool || !allAssigned || transfer.isPending}
                onClick={submit}
                className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm disabled:opacity-50"
              >
                {t("teacherTransfer.execute")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
